import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import type { NavItem } from "./types";

interface SectionPickerProps {
  /** Title of the context; undefined while loading. */
  title: string | null | undefined;
  items: NavItem[];
  activeChain: NavItem[];
  /**
   * "drawer": the mobile header picker (P2). "menu": the compact picker in
   * the side panel header (R6); a popover on desktop, the drawer on mobile.
   */
  variant: "drawer" | "menu";
  className?: string;
}

/**
 * The sections as a tree that looks like the context sidebar: icons,
 * sub-sections indented under a guide line, arrows to expand parents. The
 * active branch starts expanded.
 */
function SectionTree({
  items,
  activeKeys,
  onNavigate,
  depth = 0,
}: {
  items: NavItem[];
  activeKeys: Set<string>;
  onNavigate: () => void;
  depth?: number;
}) {
  return (
    <ul
      className={cn(
        "space-y-0.5",
        depth > 0 && "ml-3.5 mt-0.5 border-l border-border pl-2",
      )}
    >
      {items.map((item) => (
        <SectionTreeItem
          key={item.key}
          item={item}
          activeKeys={activeKeys}
          onNavigate={onNavigate}
          depth={depth}
        />
      ))}
    </ul>
  );
}

function SectionTreeItem({
  item,
  activeKeys,
  onNavigate,
  depth,
}: {
  item: NavItem;
  activeKeys: Set<string>;
  onNavigate: () => void;
  depth: number;
}) {
  const isActive = activeKeys.has(item.key);
  const hasChildren = !!item.children?.length;
  const [open, setOpen] = useState(isActive);
  const Icon = item.icon;
  // Parents of the active item are only highlighted, the item itself is marked.
  const isCurrent = isActive && !hasChildren;

  return (
    <li>
      <div className="relative flex items-center">
        <Link
          to={item.href}
          onClick={onNavigate}
          aria-current={isCurrent ? "page" : undefined}
          className={cn(
            "flex flex-1 min-w-0 items-center gap-2 rounded-md px-2 py-2 md:py-1.5",
            "text-sm transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            isActive && "font-medium",
            isCurrent && "bg-accent text-primary",
            hasChildren && "pr-8",
          )}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span className="truncate">{item.label}</span>
          {!hasChildren && item.badge ? (
            <span className="ml-auto text-xs text-muted-foreground">
              {item.badge}
            </span>
          ) : null}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-label={
              open ? `Collapse ${item.label}` : `Expand ${item.label}`
            }
            onClick={() => setOpen((value) => !value)}
            className="absolute right-1 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                !open && "-rotate-90",
              )}
            />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <SectionTree
          items={item.children!}
          activeKeys={activeKeys}
          onNavigate={onNavigate}
          depth={depth + 1}
        />
      )}
    </li>
  );
}

/**
 * Lists the sections of a context, sub-sections nested. Shared by the mobile
 * header and the side panel header.
 */
export function SectionPicker({
  title,
  items,
  activeChain,
  variant,
  className,
}: SectionPickerProps) {
  const [open, setOpen] = useState(false);
  const active = activeChain[activeChain.length - 1];
  const activeKeys = new Set(activeChain.map((item) => item.key));
  const isMobile = useIsMobile();
  const asPopover = variant === "menu" && !isMobile;
  const ActiveIcon = activeChain.find((item) => item.icon)?.icon;
  const sectionLabel = activeChain.map((item) => item.label).join(" › ");

  const trigger = (
    <button
      type="button"
      // The popover trigger toggles itself.
      onClick={asPopover ? undefined : () => setOpen(true)}
      className={cn(
        "flex items-center gap-1 rounded-md px-2 py-1 -mx-2 -my-1 min-w-0",
        "text-sm font-medium text-foreground",
        "hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "transition-colors",
        className,
      )}
      data-testid={
        variant === "drawer" ? "mobile-section-picker" : "panel-section-picker"
      }
    >
      {variant === "drawer" &&
        (title ? (
          <span className="truncate max-w-[140px]">{title}</span>
        ) : (
          <Skeleton className="h-4 w-24" />
        ))}
      {sectionLabel && (
        <>
          {variant === "drawer" && (
            <span className="text-muted-foreground shrink-0">/</span>
          )}
          {ActiveIcon && (
            <ActiveIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span
            className={cn(
              "truncate",
              variant === "drawer"
                ? "text-muted-foreground max-w-[120px]"
                : "max-w-[200px]",
            )}
          >
            {variant === "drawer" ? active?.label : sectionLabel}
          </span>
        </>
      )}
      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 ml-0.5" />
    </button>
  );

  const tree = (
    <SectionTree
      items={items}
      activeKeys={activeKeys}
      onNavigate={() => setOpen(false)}
    />
  );

  // Desktop panel: a popover next to the trigger.
  if (asPopover) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-64 p-1.5 max-h-[70vh] overflow-y-auto"
        >
          <nav aria-label="Sections">{tree}</nav>
        </PopoverContent>
      </Popover>
    );
  }

  // Mobile (main header and panel): the bottom drawer.
  return (
    <>
      {trigger}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base">{title ?? "…"}</DrawerTitle>
          </DrawerHeader>
          <nav
            aria-label="Sections"
            className="px-4 pb-6 max-h-[70vh] overflow-y-auto"
          >
            {tree}
          </nav>
        </DrawerContent>
      </Drawer>
    </>
  );
}
